import { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '../../supabaseClient';
import { useAuth } from '../../contexts/AuthContext';

const ws = () => supabase.schema('workspace');

/**
 * 페이지 단위 선택 공유(초대) 데이터 훅.
 *
 * 데이터 모델:
 *  - workspace.pages.restricted (boolean) — 페이지 제한 여부
 *  - workspace.page_permissions (page_id, principal_type 'user'|'group',
 *      principal_id, level 'full'|'edit'|'comment'|'view')
 *  - workspace.members (org_id, user_id, display_name, avatar_url)
 *  - workspace.groups (org_id, id, name)
 *
 * 쓰기 권한은 RLS 가 생성자/admin 만 허용한다.
 */
export default function useShareInvite(pageId) {
  const { membership } = useAuth();
  const orgId = membership?.org_id ?? null;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [restricted, setRestricted] = useState(false);
  const [perms, setPerms] = useState([]);     // page_permissions 행 목록
  const [members, setMembers] = useState([]); // 조직 멤버
  const [groups, setGroups] = useState([]);   // 조직 그룹

  // 초기 로드: restricted + 권한 목록 + 멤버/그룹
  useEffect(() => {
    let active = true;
    setLoading(true);
    setError('');
    (async () => {
      const [pageRes, permRes, memberRes, groupRes] = await Promise.all([
        ws().from('pages').select('restricted').eq('id', pageId).maybeSingle(),
        ws()
          .from('page_permissions')
          .select('page_id, principal_type, principal_id, level')
          .eq('page_id', pageId),
        orgId
          ? ws()
              .from('members')
              .select('user_id, display_name, avatar_url')
              .eq('org_id', orgId)
          : Promise.resolve({ data: [], error: null }),
        orgId
          ? ws().from('groups').select('id, name').eq('org_id', orgId)
          : Promise.resolve({ data: [], error: null }),
      ]);
      if (!active) return;

      if (pageRes.error) {
        setError('공유 권한 설정을 불러오지 못했습니다.');
      } else {
        setRestricted(!!pageRes.data?.restricted);
      }
      if (!permRes.error) setPerms(permRes.data || []);
      if (!memberRes.error) setMembers(memberRes.data || []);
      if (!groupRes.error) setGroups(groupRes.data || []);

      setLoading(false);
    })();
    return () => { active = false; };
  }, [pageId, orgId]);

  // user_id / group id 로 표시 이름·아바타 조회
  const memberMap = useMemo(() => {
    const m = new Map();
    members.forEach((x) => m.set(x.user_id, x));
    return m;
  }, [members]);

  const groupMap = useMemo(() => {
    const m = new Map();
    groups.forEach((x) => m.set(x.id, x));
    return m;
  }, [groups]);

  // 현재 권한 목록을 표시용으로 가공
  const entries = useMemo(() => {
    return perms.map((p) => {
      if (p.principal_type === 'group') {
        const g = groupMap.get(p.principal_id);
        return {
          ...p,
          name: g?.name || '알 수 없는 그룹',
          avatarUrl: null,
        };
      }
      const u = memberMap.get(p.principal_id);
      return {
        ...p,
        name: u?.display_name || '알 수 없는 사용자',
        avatarUrl: u?.avatar_url || null,
      };
    });
  }, [perms, memberMap, groupMap]);

  // 이미 추가된 principal 은 검색 후보에서 제외
  const addedKeys = useMemo(() => {
    const s = new Set();
    perms.forEach((p) => s.add(`${p.principal_type}:${p.principal_id}`));
    return s;
  }, [perms]);

  /** 페이지 제한 토글. pages.restricted 업데이트. */
  const setPageRestricted = useCallback(async (next) => {
    const prev = restricted;
    setRestricted(next);            // 낙관적
    setError('');
    const { error: err } = await ws()
      .from('pages')
      .update({ restricted: next })
      .eq('id', pageId);
    if (err) {
      setRestricted(prev);          // 롤백
      setError('제한 설정을 변경하지 못했습니다.');
      return false;
    }
    return true;
  }, [pageId, restricted]);

  /** 권한 대상 추가/갱신. upsert onConflict (page_id, principal_type, principal_id). */
  const upsertPermission = useCallback(async (principalType, principalId, level) => {
    setError('');
    const row = {
      page_id: pageId,
      principal_type: principalType,
      principal_id: principalId,
      level,
    };
    const { error: err } = await ws()
      .from('page_permissions')
      .upsert(row, { onConflict: 'page_id,principal_type,principal_id' });
    if (err) {
      setError('권한을 추가하지 못했습니다.');
      return false;
    }
    setPerms((cur) => {
      const idx = cur.findIndex(
        (p) => p.principal_type === principalType && p.principal_id === principalId,
      );
      if (idx >= 0) {
        const copy = cur.slice();
        copy[idx] = { ...copy[idx], level };
        return copy;
      }
      return [...cur, row];
    });
    return true;
  }, [pageId]);

  /** 권한 등급 변경 (기존 항목). */
  const updateLevel = useCallback(async (principalType, principalId, level) => {
    return upsertPermission(principalType, principalId, level);
  }, [upsertPermission]);

  /** 권한 제거. delete. */
  const removePermission = useCallback(async (principalType, principalId) => {
    const prev = perms;
    setPerms((cur) => cur.filter(
      (p) => !(p.principal_type === principalType && p.principal_id === principalId),
    ));
    setError('');
    const { error: err } = await ws()
      .from('page_permissions')
      .delete()
      .eq('page_id', pageId)
      .eq('principal_type', principalType)
      .eq('principal_id', principalId);
    if (err) {
      setPerms(prev);               // 롤백
      setError('권한을 제거하지 못했습니다.');
      return false;
    }
    return true;
  }, [pageId, perms]);

  return {
    loading,
    error,
    restricted,
    entries,
    members,
    groups,
    addedKeys,
    setPageRestricted,
    upsertPermission,
    updateLevel,
    removePermission,
  };
}
